import SelectArrow from "@/public/ArrowDown.svg";
import { PortableTextBlock } from "@sanity/types";
import SMaxWidthContainer from 'components/framework/SMaxWidthContainer';
import DataLoader from "components/program/DataLoader";
import PostersView from "components/program/PostersView";
import WideView from "components/program/WideView";
import RichText, { createRichTextQueryFragment } from "components/richtext/RichText";
import { format, parse } from "date-fns";
import locale from "date-fns/locale/nb";
import { useBaseTitle } from "lib/client/useTitle";
import { setDefaultCacheHeaders } from "lib/server/cache";
import { toRem } from "lib/client/fonts";
import { getDaysWithShows } from "lib/server/movieinfo";
import { fwpakkeQuery, getServerData } from "lib/server/sanity";
import { ICommonAppData, IProgramPage } from "lib/types/sanity";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import Head from "next/head";
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { groq } from "next-sanity";
import dynamic from "next/dynamic";


const DynamicAdnuntiusAd = dynamic(() =>
	import('components/framework/AdnuntiusAd').then((mod) => mod.default),
	{ ssr: false }
);


//#region [Other]getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {
	setDefaultCacheHeaders(context as GetServerSidePropsContext);

	const programPageData = await fwpakkeQuery<IProgramPage>(PROGRAM_PAGE_QUERY);
	const daysWithShows = await getDaysWithShows(false, ["KUL"]);

	return {
		props: {
			daysWithShows: daysWithShows,
			infoText: (programPageData?.showInfoText && programPageData?.topInfoText?.length > 0) ? programPageData?.topInfoText : null
		}, // will be passed to the page component as props

	}
});
//#endregion

//#region [Styles]
const SSectionHeader = styled.header`
	text-align: center;
	margin-top: 32px;
	margin-bottom: 32px;

	h1 {
		font-size: ${toRem(31)};
		text-transform: uppercase;
	}
`;

const SDateSelector = styled.span`
	position: relative;
	color: var(--color_dateselector_text, var(--color_ticketbutton_text, #fff));
	select {
		background-color: var(--color_dateselector_bg, var(--color_ticketbutton_bg, #000));
		color: var(--color_dateselector_text, var(--color_ticketbutton_text, #fff));
		font-size: ${toRem(20)};

		@media screen and (min-width: 641px) {
			font-size: 1rem;
		}

		padding: 0.5em 2.5em 0.5em 0.5em;
		appearance: none;
		border: none;
	}
	svg {
		position: absolute;
		width: 21px;
		height: 13px;
		right: 5px;
		top: 50%;
		transform: translateY(-50%);
		pointer-events: none;
	}
`;

const SInfoText = styled(RichText)`
	text-align: left;
	margin: 0 16px;

`;

const SDataLoader = styled(DataLoader)`
	&[aria-busy='true'] {
		min-height: 90vh;
	}
`;
//#endregion

//#region [Props]
type ProgramProps = {
	daysWithShows: string[] | undefined;
	appData: ICommonAppData;
	infoText: PortableTextBlock[] | null;
};
//#endregion

//#region [Component]
export default function Program({ daysWithShows, appData, infoText }: ProgramProps) {

	const datesWithShows = useMemo(() => {
		return daysWithShows?.map(d => {
			const parsed = parse(d, "yyyy-MM-dd'T'HH:mm:ss", new Date(), { locale });
			const formatted = format(parsed, 'EEEE dd. MMMM', { locale });
			return { str: d, date: parsed, display: formatted.charAt(0).toUpperCase() + formatted.slice(1) }
		});
	}, [daysWithShows]);

	const router = useRouter();

	const [selectedDate, setSelectedDate] = useState(() => {
		let routerDate: string | undefined;
		if (router.query.dato) {
			if (Array.isArray(router.query.dato) && router.query.dato.length === 1) {
				routerDate = router.query.dato[0];
			} else if (!Array.isArray(router.query.dato)) {
				routerDate = router.query.dato;
			}
		}

		if (routerDate) {
			if (/^\d{4}-\d{2}-\d{2}$/.test(routerDate.trim())) {
				return routerDate;
			} else if (appData.settings.program_hasalldays && routerDate.trim() === "alle") {
				return routerDate;
			}
		}

		if (appData.settings.program_hasalldays) {
			return "alle";
		}

		return datesWithShows?.[0]?.str?.substring(0, 10) ?? format(new Date(), 'yyyy-MM-dd', { locale });
	});




	useEffect(() => {
		// Update the query string with the date
		if (selectedDate && selectedDate !== router.query.dato) {
			router.replace(`/program?dato=${selectedDate}`, undefined, { shallow: true });
		}
	}, [selectedDate, router]);

	let View: any | undefined;
	switch (appData.settings.program_view) {
		case 'posters':
			View = PostersView;
			break;
		case 'wide':
			View = WideView;
			break;
		default:
			View = PostersView;
	}

	const title = useBaseTitle(appData.settings, "Kinoprogram");
	return <>
		<SMaxWidthContainer as="section">
			<Head>
				<title>{title}</title>
				<meta property="og:title" content={title} key="title" />
			</Head>
			<SSectionHeader>
				<h1>Kinoprogrammet</h1>
				{infoText && <SInfoText value={infoText} disableCustomComponents />}

				{(datesWithShows?.length ?? 0) > 0 && (
					<SDateSelector>
						<select value={selectedDate} onChange={(ev) => setSelectedDate(ev.currentTarget.value)} aria-label="Velg dato">
							{appData.settings.program_hasalldays && <option value="alle">Alle dager</option>}
							{datesWithShows!.map(d => <option key={d.str.substring(0, 10)} value={d.str.substring(0, 10)}>{d.display}</option>)}
						</select>
						<SelectArrow />
					</SDateSelector>
				)}
			</SSectionHeader>

			{/** SHOW THE VIEW */}
			<SDataLoader date={selectedDate} loaderText="Henter kinoprogrammet...">
				<View allowTitleOverride={appData.settings.canOverrideMovieTitles} appData={appData}/>
			</SDataLoader>


		</SMaxWidthContainer>
		{<DynamicAdnuntiusAd appData={appData} index={1} hideOnMobile={false} hideOnDesktop={true} debugLabel="Program bottom"/>}
	</>
}
//#endregion

//#region [Other]
const PROGRAM_PAGE_QUERY = groq`*[_id == "program"][0] {
	...,
	${createRichTextQueryFragment("topInfoText")}
}`;
//#endregion